/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
/* eslint-disable comma-dangle */
/*
Contains all Supabase queries related to Discord
*/
import { SupabaseClient } from "@supabase/supabase-js";
import dayjs from "dayjs";
import moment from "moment";

import { DiscordGuilds } from "../../types/discord_guilds";
import { DiscordMembers } from "../../types/discord_members";
import { DiscordRoles } from "../../types/discord_roles";
import { Tags } from "../../types/tags";
import {
  ActiveCohortMemberIdsRpc,
  ActiveMembersOverTimeRpc,
  ActiveTimeForMessaging,
  AllMembers,
  AtRiskMember,
  DiscordMemberChannelCounts,
  DiscordMemberRpc,
  DiscordRecentMemberActivity,
  DiscordTriggersRpc,
  GroupBy,
  MostActiveChannelsRpc,
  MostActiveMembersRpc,
  NewCohortMemberIdsRpc,
  NewMembers,
  PopularThreadsRpc,
  RetentionData,
  TimePeriod,
  TriggerExecutionsRpc,
} from "./discordTypes";
import { getSupaClient, getWorkspace } from "./supabase";

const dateFormat = "MM/DD/YY";

/**
 * Get the trigger executions for a given trigger id
 * @param triggerId
 * @param communityId
 * @param workspaceId
 * @returns a list of trigger executions and related member data
 */
const getTriggerExecutions = async (
  triggerId: string,
  communityId: string,
  workspaceId: string,
  startDate?: string,
  endDate?: string
) => {
  const supaClient = getSupaClient();
  const workspace = getWorkspace(supaClient);
  let res;
  if (startDate && endDate) {
    res = await supaClient.rpc<TriggerExecutionsRpc>(
      "get_executions_for_trigger_daterange",
      {
        trigger: triggerId,
        _community_id: communityId,
        include_relatives: true,
        _workspace: workspaceId,
        start_time: startDate,
        end_time: endDate,
      }
    );
  } else {
    res = await supaClient.rpc<TriggerExecutionsRpc>(
      "get_executions_for_trigger",
      {
        trigger: triggerId,
        _community_id: communityId,
        include_relatives: true,
        _workspace: workspaceId,
      }
    );
  }
  if (res.error) {
    console.log("Error in getTriggerExecutions:", res.error);
  }
  return res.data || [];
};
export { getTriggerExecutions };

/**
 * Loads a list of triggers for a given guild
 * @param guildId the guild id
 * @returns a list of triggers including execution count
 */
const getDiscordTriggers = async (guildId: string, workspace: string) => {
  const supaClient = getSupaClient();
  const { data, error } = await supaClient.rpc<DiscordTriggersRpc>(
    "get_discord_triggers",
    { guild_id: guildId, _workspace: workspace }
  );
  if (error) {
    console.log("Error at getDiscordTriggers:", error);
  }
  return data || [];
};
export { getDiscordTriggers };

/**
 * Get a list of questions and responses coming from a trigger executions.
 * These questions and responses have to be filtered out by one of these:
 *  - A trigger execution;
 *  - A trigger.
 *  - A community and workspace.
 *  - A community.
 * @returns A list of question and responses.
 */
const getDiscordQuestionsAndResponses = async (execution: any) => {
  const supaClient = getSupaClient();

  const { data, error } = await supaClient.rpc<any>("trigger_runs", {
    workspace_id: execution.workspace,
    _community_id: execution.communityId,
    _trigger_id: execution.triggerId,
    ...(execution.rootExecutionId
      ? { _root_execution_id: execution.rootExecutionId }
      : null),
    ...(Object.keys(execution).includes("includeRelatives")
      ? { include_relatives: execution.includeRelatives }
      : null),
    task_types: [
      "send_message",
      "send_survey",
      "send_multiple_select",
      "open_modal",
    ],
  });

  if (error) {
    console.log("Error at getDiscordQuestionAndResponses:", error);
  }
  return data || [];
};
export { getDiscordQuestionsAndResponses };

/**
 * Get a list of questions and responses coming from a trigger executions in a given date range.
 * These questions and responses have to be filtered out by one of these:
 *  - A trigger execution;
 *  - A trigger.
 *  - A community and workspace.
 *  - A community.
 * @returns A list of question and responses.
 */
const getDiscordQuestionsAndResponsesDateRange = async (execution: any) => {
  const supaClient = getSupaClient();

  const { data, error } = await supaClient.rpc<any>("trigger_runs_daterange", {
    workspace_id: execution.workspace,
    _community_id: execution.communityId,
    _trigger_id: execution.triggerId,
    ...(execution.rootExecutionId
      ? { _root_execution_id: execution.rootExecutionId }
      : null),
    ...(Object.keys(execution).includes("includeRelatives")
      ? { include_relatives: execution.includeRelatives }
      : null),
    task_types: [
      "send_message",
      "send_survey",
      "send_multiple_select",
      "open_modal",
    ],
    start_time: execution.startDate,
    end_time: execution.endDate,
  });

  if (error) {
    console.log("Error at getDiscordQuestionsAndResponsesDateRange:", error);
  }
  return data || [];
};
export { getDiscordQuestionsAndResponsesDateRange };

/* Loads new joined Discord members for a given guild between two dates,
 * grouped by month or week
 * @param guildId the guild id
 * @param startTime start of period
 * @param endTime end of period
 * @param timePeriod "week" or "month"
 * @returns A list of new discord members grouped by month or week
 */
const getNewMembers = async (
  startTime: string,
  endTime: string,
  timePeriod: TimePeriod
) => {
  const supaClient = getSupaClient();
  const guildId = localStorage.getItem("guild-id");
  const workspaceId =
    JSON.parse(localStorage.getItem("supabase.auth.token") || "{}")
      ?.currentSession.user?.app_metadata.workspace || "";

  if (workspaceId) {
    const params = {
      guild: guildId,
      start_time: startTime,
      end_time: endTime,
      time_period: timePeriod,
      workspace: workspaceId,
    };
    const { data, error } = await supaClient.rpc<NewMembers>(
      "discord_new_members",
      params
    );
    if (error) {
      console.log("Error at getNewMembers:", error);
    }
    return data || [];
  }
  return [];
};
export { getNewMembers };

/* Loads average number of messages in Discord grouped by day of week or hour of the day
 * @param endTime end of period
 * @param startTime start of period
 * @param guildId the guild id
 * @param groupBy "daily" or "hourly"
 * @returns A list of average number of messages and its time period
 */
const getActiveTime = async (
  startTime: string,
  endTime: string,
  groupBy: GroupBy
) => {
  const supaClient = getSupaClient();
  const guildId = localStorage.getItem("guild-id");
  const workspaceId =
    JSON.parse(localStorage.getItem("supabase.auth.token") || "{}")
      ?.currentSession.user?.app_metadata.workspace || "";

  if (workspaceId) {
    const params = {
      guild_id: guildId,
      start_time: startTime,
      end_time: endTime,
      group_by: groupBy,
    };
    let supabaseRpc = "discord_messages_count_average_hourly";
    if (groupBy === GroupBy.Daily) {
      supabaseRpc = "discord_messages_count_average_daily";
    }

    const { data, error } = await supaClient.rpc<ActiveTimeForMessaging>(
      supabaseRpc,
      params
    );

    if (error) {
      console.log("Error at getActiveTime:", error);
    }
    return data || [];
  }
  return [];
};
export { getActiveTime };

/**
 * Returns the discord roles of a guild
 *
 * @param guildId - guild id
 * @returns an array of roles
 */
const getGuildRoles = async (guildId: string) => {
  const supaClient = getSupaClient();
  const { data, error } = await supaClient
    .from<DiscordRoles>("discord_roles")
    .select(
      `
        discord_id,
        name
      `
    )
    .match({ guild: guildId });
  if (error) console.error("Error in getGuildRoles:", error);
  return data;
};
export { getGuildRoles };

/**
 * Get the given team's workspace id
 * @param {string} teamId
 * @returns {null or int} null if a workspace wasn't found, or id if found
 */
const getWorkspaceId = async (guildId: string) => {
  const supaClient = getSupaClient();
  const { data, error } = await supaClient
    .from<DiscordGuilds>("discord_guilds")
    .select("workspace")
    .match({ discord_id: guildId });
  if (error) {
    console.log("error in getWorkspaceId", error);
    return null;
  }
  console.log("success in getWorkspaceId", data);
  return data![0].workspace;
};
export { getWorkspaceId };

/*
Purpose: Get discord channels for a given team
Params:
  guildId: guild id
*/
async function getDiscordChannels(guildId: string) {
  const supaClient = getSupaClient();
  const { data, error } = await supaClient
    .from("discord_channels")
    .select()
    .filter("guild", "eq", guildId)
    .neq("type", "GUILD_VOICE")
    .neq("type", "GUILD_CATEGORY");
  if (error) console.log("error in getDiscordChannels", error);
  return data;
}
export { getDiscordChannels };

/*
Purpose: Get tags rows for a given guild
Params:
  guildId: guild id
*/
async function getGuildTags(guildId: string) {
  const supaClient = getSupaClient();
  const { data, error } = await supaClient
    .from<Tags>("tags")
    .select()
    .filter("guild", "eq", guildId);
  if (error) console.log("error in getGuildTags", error);
  return data;
}
export { getGuildTags };

/*
Purpose: Get initial 10k users sorted by activity counts
Params:
  guildId: discord guild id
*/
async function getDiscordInitialMembers(guildId: string) {
  const supaClient = getSupaClient();
  let initialMembers: DiscordMembers[] = [];
  const pageSize = 10000;
  let cursor = 0;
  const { data, error } = await supaClient
    .from<DiscordMembers>("discord_members")
    .select(
      `
        id,
        username,
        discord_id,
        discriminator,
        avatar,
        joined_at,
        discord_roles(discord_id, name),
        tags(id, name, color)
      `
    )
    .eq("guild", guildId)
    .neq("is_bot", true)
    .eq("is_deleted", false)
    .order("joined_at", { ascending: false })
    .range(0, pageSize);
  // console.log("Members:", data);
  if (data) {
    initialMembers = initialMembers.concat(data);
    // console.log("initialMembers:", initialMembers);
    if (data.length >= pageSize) {
      cursor = pageSize;
      // console.log("cursor", cursor);
    }
  }
  if (error) {
    console.log("error in getAllUsersPaginated", error);
  }
  return { initialMembers: initialMembers as AllMembers[], cursor };
}
export { getDiscordInitialMembers };

async function getMembersStorageFile(guildId: string) {
  const supaClient = getSupaClient();
  const { data, error } = await supaClient.storage.from("members").list();
  if (error) {
    console.log("Error getting members storage file", error);
    console.log("error", error);
    return [];
  }

  //order data by created at desc and return the first element
  return data
    ?.filter((filename) => filename.name.includes(guildId))
    .sort((fileA, fileB) => {
      return (
        new Date(fileB.created_at).getTime() -
        new Date(fileA.created_at).getTime()
      );
    })[0];
}

async function getAllMembers(guildId: string) {
  const arrayFieldParser = (
    line: string,
    characterPosition: number,
    character: string,
    spacesStartWith: number,
    stopCharacter: string
  ) => {
    let characterAcum = 0;
    let characterIndex = 0;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === character) {
        characterAcum++;
      }
      if (characterAcum === characterPosition) {
        characterIndex = i;
        break;
      }
    }

    let arrayValue = "";
    for (let i = characterIndex + spacesStartWith; i < line.length; i++) {
      if (line[i] === stopCharacter) {
        arrayValue += line[i];
        break;
      }
      arrayValue += line[i];
    }

    if (arrayValue[0] === '"') {
      arrayValue = arrayValue.slice(1);
    }

    return arrayValue.replaceAll('""', '"');
  };

  const lastFile = await getMembersStorageFile(guildId);

  return new Promise(async (resolve, reject) => {
    if (!lastFile) {
      return resolve([]);
    }

    const supaClient = getSupaClient();
    const { data, error } = await supaClient.storage
      .from("members")
      .download((lastFile as any).name);

    if (error) {
      console.log("error getting members", error);
      reject(error);
    }
    const reader = new FileReader();
    reader.readAsText(data as Blob);
    reader.onload = () => {
      const json = (reader.result as string).split("\n");
      //Create elements for each row in the CSV file.
      //First row is the header
      const headers = json[0].split(",");
      const rows = json.slice(1);
      const members = rows.map((row, index) => {
        const cols = row.split(",");
        //Last line is empty
        if (index === rows.length - 1) return;
        let memberObject: Record<string, any> = {};
        headers.forEach((header: any, index) => {
          if (header === "discord_roles") {
            memberObject[header] = JSON.parse(
              arrayFieldParser(row, 6, ",", 1, "]")
            );
          } else if (header === "tags") {
            memberObject[header] = JSON.parse(
              arrayFieldParser(row, 1, "]", 2, "]")[0] === ","
                ? arrayFieldParser(row, 1, "]", 3, "]")
                : arrayFieldParser(row, 1, "]", 2, "]")
            );
          } else {
            memberObject[header] = cols[index];
          }
        });
        return memberObject;
      });
      //remove last element, this is because the csv has an empty line at the end
      members.pop();
      resolve(members);
    };
  });
}
export { getAllMembers };

/*
Purpose: Get all total members from a guild
Params:
  guildId: discord guild id
*/
async function getDiscordTotalGuildMembers(guildId: string) {
  const supaClient = getSupaClient();

  const { data, error, count } = await supaClient
    .from<DiscordMembers>("discord_members")
    .select(
      `
        id
      `,
      { count: "exact", head: true }
    )
    .eq("guild", guildId)
    .eq("is_deleted", false)
    .neq("is_bot", true);
  if (error) {
    console.log("error in getDiscordTotalGuildMembers", error);
  }
  return { totalMembersCounter: count };
}
export { getDiscordTotalGuildMembers };

/*
Purpose: Get all users from a cursor to the remaining.
Params:
  teamId: team slack_id
*/
async function getDiscordMembersPaginatedFrom(guildId: string, cursor: number) {
  const supaClient = getSupaClient();
  let allUsers: DiscordMembers[] = [];
  let done = false;
  const pageSize = 10000;
  let from = cursor;
  /* eslint-disable no-await-in-loop */
  while (!done) {
    const { data, error } = await supaClient
      .from<DiscordMembers>("discord_members")
      .select(
        `
        id,
        username,
        discord_id,
        discriminator,
        avatar,
        joined_at,
        discord_roles(discord_id, name),
        tags(id, name, color)
      `
      )
      .eq("guild", guildId)
      .neq("is_bot", true)
      .eq("is_deleted", false)
      .order("joined_at", { ascending: false })
      .range(from, from + pageSize);

    if (data) {
      allUsers = allUsers.concat(data);
      if (data.length < pageSize) {
        done = true;
      } else {
        from += pageSize;
      }
    }
    if (error) {
      console.log("error in getAllUsersPaginated", error);
    }
  }
  /* eslint-enable no-await-in-loop */
  return allUsers;
}
export { getDiscordMembersPaginatedFrom };

async function getDiscordMemberTags(guild: string) {
  const supaClient = getSupaClient();
  const { data, error } = await supaClient
    .from<any>("tags")
    .select(
      `
      id,
      name,
      color,
      discord_members(id)
    `
    )
    .eq("guild", guild);

  if (error) {
    console.log("error in getDiscordMemberTags", error);
    return;
  }

  const mapToMembers: Record<string, any> = {};
  data.forEach((group) => {
    group.discord_members.forEach((member: { id: string }) => {
      if (!mapToMembers[member.id]) {
        mapToMembers[member.id] = { id: member.id, tags: [] };
      }
      mapToMembers[member.id].tags.push({
        id: group.id,
        name: group.name,
        color: group.color,
      });
    });
  });
  const finalTransformedStructure = Object.values(mapToMembers);

  return finalTransformedStructure;
}
export { getDiscordMemberTags };

/**
 * Get the 10 most active Discord channels for a period of time
 *
 * @param startTime - start of time period
 * @param endTime - end of time period
 * @param guildId - guild id
 * @returns - An array of objects containing the 10 most active Discord channels for a time period
 */
const getDiscordMostActiveChannels = async (
  startTime: string,
  endTime: string,
  guildId: string | null
): Promise<MostActiveChannelsRpc[] | null> => {
  const supaClient = getSupaClient();
  const params = {
    start_time: startTime,
    end_time: endTime,
    guild_id: guildId,
  };

  const { data, error } = await supaClient.rpc(
    "discord_most_active_channels",
    params
  );
  if (error) console.error("error in getDiscordMostActiveChannels", error);
  return data;
};
export { getDiscordMostActiveChannels };

/**
 * Purpose: Get the 5 most active Discord members for a period of time
 *
 * @param startTime - start of time period
 * @param endTime - end of time period
 * @param guildId - the guild id
 * @param workspaceId - the workspace id
 * @param channelId - the channel id (optional)
 * @returns - An array of objects containing the five most active Discord members for a time period
 */
const getDiscordMostActiveMembers = async (
  startTime: string,
  endTime: string,
  guildId: string | null,
  workspaceId: string,
  channelId?: string
): Promise<MostActiveMembersRpc[] | null> => {
  const supaClient = getSupaClient();
  const params: any = {
    start_time: startTime,
    end_time: endTime,
    guild_id: guildId,
    workspace_id: workspaceId,
  };
  let results: MostActiveMembersRpc[] | [];

  if (channelId) {
    params.channel_id = channelId;
    const { data, error } = await supaClient.rpc(
      "discord_most_active_members",
      params
    );
    if (error) console.log("Error in getDiscordMostActiveMembers:", error);
    results = data || [];
  } else {
    const { data, error } = await supaClient.rpc(
      "discord_most_active_members_overall",
      params
    );
    if (error) console.error("error in getDiscordMostActiveMembers", error);
    results = data || [];
  }
  return results;
};
export { getDiscordMostActiveMembers };

/**
 * Purpose: Get the most active members for a given guild and time period.
 *
 * @param startTime - start of time period
 * @param endTime - end of time period
 * @param guildId - guild id
 * @returns - An array of objects containing the most active Discord members
 */
const getDiscordActiveMembers = async (
  startTime: string,
  endTime: string,
  guildId: string
): Promise<DiscordMemberRpc[] | null> => {
  const supaClient = getSupaClient();
  const params = {
    start_time: startTime,
    end_time: endTime,
    guild_id: guildId,
  };

  const { data, error } = await supaClient.rpc(
    "discord_active_members",
    params
  );
  if (error) console.error("error in getActiveUsers", error);
  return data;
};
export { getDiscordActiveMembers };

/**
 * Get the Discord active member count over a time period, grouped by day
 *
 * @param startTime - start of time period
 * @param endTime - end of time period
 * @param guildId - Discord guild id
 * @param workspaceId - workspace id
 * @returns An array of objects containing the day date and the total Discord active members count for that day
 */
async function getDiscordActiveMembersOverTime(
  startTime: string,
  endTime: string,
  guildId: string | null,
  workspaceId: string
): Promise<ActiveMembersOverTimeRpc[] | null> {
  const supaClient = getSupaClient();
  const defaultTimeFormat = "MM/DD/YY";
  const params = {
    start_time: moment(startTime).format(defaultTimeFormat),
    end_time: moment(endTime).format(defaultTimeFormat),
    guild_id: guildId,
    workspace: workspaceId,
  };
  const { data, error } = await supaClient.rpc(
    "discord_active_members_count_timeperiod",
    params
  );
  if (error) console.error("error in getDiscordActiveMembersOverTime", error);
  return data;
}
export { getDiscordActiveMembersOverTime };

/**
 * Get the active Discord members count over a time period grouped by week
 *
 * @param startTime - start of time period
 * @param endTime - end of time period
 * @param guildId - discord guild id
 * @param workspaceId - workspace id
 * @returns An array of objects containing the week date and the Discord active members count for that week
 */
async function getDiscordActiveMembersOverTimeByWeek(
  startTime: string,
  endTime: string,
  guildId: string | null,
  workspaceId: string
): Promise<ActiveMembersOverTimeRpc[] | null> {
  const supaClient = getSupaClient();
  const defaultTimeFormat = "MM/DD/YY";
  const params = {
    start_time: moment(startTime).format(defaultTimeFormat),
    end_time: moment(endTime).format(defaultTimeFormat),
    guild_id: guildId,
    workspace: workspaceId,
  };
  const { data, error } = await supaClient.rpc(
    "discord_active_members_count_weekly",
    params
  );
  if (error)
    console.error("error in getDiscordActiveMembersOverTimeByWeek", error);
  return data;
}
export { getDiscordActiveMembersOverTimeByWeek };

/**
 * Purpose: get at-risk members for a given guild
 *
 * @param guildId - guild id
 * @returns - An aray of objects with the at-risk members
 */
const getDiscordAtRiskMembers = async (
  guildId: string // guildId
  // count?: boolean
): Promise<AtRiskMember[] | null> => {
  const supaClient = getSupaClient();
  const params = {
    guild_id: guildId,
  };
  const response = await supaClient.rpc<AtRiskMember>(
    "discord_at_risk_members",
    params
  );
  if (response.error) console.error("error in getAtRiskUsers", response.error);
  return response.data;
};
export { getDiscordAtRiskMembers };

/*
 * Purpose: Get retention data
 * Params:
    guildid: guild id discord
    interval: how many months to pull the retentin data for
    workspace:
    roleId: role id to filter by

 */

const getDiscordRetentionData = async (
  guildId: string,
  workspaceId: string,
  interval: string,
  roleId?: string | null
): Promise<RetentionData[] | null> => {
  let rpcName: "discord_retention_by_role" | "discord_retention_total";
  const supaClient = getSupaClient();
  let params: {
    guild_id: string;
    workspace: string;
    interval_period: string;
    _role?: string;
  } = {
    guild_id: guildId,
    workspace: workspaceId,
    interval_period: interval,
  };

  if (roleId) {
    params = {
      ...params,
      _role: roleId,
    };
    rpcName = "discord_retention_by_role";
  } else {
    rpcName = "discord_retention_total";
  }
  const { data, error } = await supaClient.rpc(rpcName, params);
  if (error) console.error("error getting retention RPC ", error);
  return data;
};

export { getDiscordRetentionData };

/**
 * Loads Discord members from an array of member ids
 * @param memberIds an array of Discord member ids
 * @returns an array of Discord members
 */
const getCohortMembers = async (
  // memberIds: string[],
  memberIds: NewCohortMemberIdsRpc[] | ActiveCohortMemberIdsRpc[] | string[],
  guildId: string
) => {
  const supaClient = getSupaClient();
  const params = {
    member_ids: memberIds,
    guild_id: guildId,
  };
  const { data, error } = await supaClient.rpc(
    "discord_members_from_list",
    params
  );
  // const { data, error } = await supaClient
  //   .from("discord_members")
  //   .select(
  //     `username,
  //       discord_id,
  //       avatar,
  //       discord_roles(discord_id, name),
  //       tags(id, name, color)`
  //   )
  //   .filter("guild", "eq", guildId)
  //   .in("discord_id", memberIds);
  if (error) {
    console.log("Error in getCohortMembers:", error);
  }
  if (data) {
    console.log("data loaded!:", data.slice(0, 10));
  }
  return data || { error };
};
export { getCohortMembers };

/**
 * Loads the new members for a given cohort
 * @param cohort - the cohort month in date formart "MM/DD/YYY"
 * @param guildId - the guild id
 * @param roleId - role id to filter by
 * @returns - a list of new cohort member ids
 */
const getNewCohortMemberData = async (
  cohort: string,
  guildId: string,
  roleId?: string | null
) => {
  const path = `/guilds/${guildId}/members/discord_new_cohort_members`;
  const supaClient = getSupaClient();
  const session = await supaClient.auth.session();
  let params: {
    cohort: string;
    guild_id: string;
    role_id?: string;
  } = {
    cohort,
    guild_id: guildId,
  };

  if (roleId) {
    params = { ...params, role_id: roleId };
  }

  const response = await fetch(`${process.env["REACT_APP_API_URL"]}${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${session?.access_token}` },
    body: JSON.stringify(params),
  });

  if (response.ok) {
    const responseJson = await response.json();
    return responseJson.data as DiscordMembers[];
  } else {
    let reason;
    try {
      reason = (await response.json()).reason;
    } catch (e: any) {
      reason = e.message ?? JSON.stringify(e);
    }

    return { error: reason };
  }
};

export { getNewCohortMemberData };

/**
 * Loads the ids for the active members of a given cohort
 * delta months after they joined the cohort
 * @param cohort - the cohort month
 * @param delta
 * @param guildId - the guild id
 * @param roleId - role id to filter by
 * @returns - a list of active member ids
 */
const getActiveCohortMembers = async (
  cohort: string,
  delta: number,
  guildId: string,
  roleId?: string | null
) => {
  let path = `/guilds/${guildId}/members/discord_active_cohort_members`;
  const supaClient = getSupaClient();
  const session = await supaClient.auth.session();
  let params: {
    guild_id: string;
    interval_period: number;
    param_retention_month: number;
    param_cohort_month: string;
    role_id?: string;
  } = {
    guild_id: guildId,
    interval_period: 6,
    param_retention_month: delta,
    param_cohort_month: cohort,
  };

  if (roleId) {
    params = { ...params, role_id: roleId };
  }
  const response = await fetch(`${process.env["REACT_APP_API_URL"]}${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${session?.access_token}` },
    body: JSON.stringify(params),
  });

  if (response.ok) {
    const responseJson = await response.json();
    return responseJson.data as DiscordMembers[];
  } else {
    let reason;
    try {
      reason = (await response.json()).reason;
    } catch (e: any) {
      reason = e.message ?? JSON.stringify(e);
    }

    return { error: reason };
  }
};

export { getActiveCohortMembers };

/**
 * Purpose: Get recent activity for a discord member over a given time period.
 * @param startTime start of time period in format "MM/DD/YY"
 * @param endTime end of time period in format "MM/DD/YY"
 * @param userId member id
 * @returns
 */
const getDiscordMemberRecentActivity = async (
  startTime: string,
  endTime: string,
  userId: string
) => {
  const supaClient = getSupaClient();
  const params = {
    start_time: startTime,
    end_time: endTime,
    user_id: userId,
  };

  const { data, error } = await supaClient.rpc<DiscordRecentMemberActivity>(
    "discord_recent_member_activity",
    params
  );
  if (error) console.error("error in getDiscordMemberRecentActivity", error);
  return data;
};
export { getDiscordMemberRecentActivity };

/**
 * Purpose: Get channel specific activity counts for a discord member (uses the discord_member_channel_counts RPC)
 * @param guildId discord_guild discord_id value
 * @param startTime start of time period in format "MM/DD/YY"
 * @param endTime end of time period in format "MM/DD/YY"
 * @param userId member id
 * @returns
 */
const getDiscordMemberChannelCounts = async (
  startTime: string,
  endTime: string,
  guildId: string,
  userId: string
) => {
  const supaClient = getSupaClient();
  const params = {
    team_id: guildId,
    user_id: userId,
    start_time: startTime,
    end_time: endTime,
  };

  const { data, error } = await supaClient.rpc<DiscordMemberChannelCounts>(
    "discord_member_channel_counts",
    params
  );
  if (error) console.error("error in getDiscordMemberChannelCounts", error);
  return data;
};
export { getDiscordMemberChannelCounts };

/**
 * Get the most popular threads for a given period of time
 * @param startTime start of time period in format "MM/DD/YY"
 * @param endTime end of time period in format "MM/DD/YY"
 * @param guildId discord_guild discord_id value
 * @returns a list of popular threads by sorted by thread message count
 */
const getPopularThreads = async (
  startTime: string,
  endTime: string,
  guildId: string,
  workspaceId: string,
  channelId: string,
  excludedChannels: string[]
) => {
  const supaClient = getSupaClient();
  const params = {
    start_time: startTime,
    end_time: endTime,
    guild_id: guildId,
    workspace_id: workspaceId,
  };

  const base = supaClient.rpc<PopularThreadsRpc>(
    "discord_popular_threads",
    params
  );

  if (channelId) {
    base.filter("channel_id", "eq", channelId);
  }
  if (excludedChannels) {
    excludedChannels.forEach((item) => {
      base.filter("channel_id", "neq", item);
    });
  }
  const { data, error } = await base;
  if (error) {
    console.log("Error in getPopularThreads:", error);
  }
  return data || [];
};
export { getPopularThreads };
